import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Star, Plus, ClipboardList, Check, Award, Users } from 'lucide-react';

const CATEGORIES = [
  { k: 'quality', label: 'Quality of work' },
  { k: 'ownership', label: 'Ownership' },
  { k: 'collaboration', label: 'Collaboration' },
  { k: 'communication', label: 'Communication' },
  { k: 'growth', label: 'Learning & growth' },
];

function StarRow({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange?.(n)}
                disabled={!onChange}
                className={`p-1 ${onChange ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}>
          <Star className={`w-5 h-5 ${value >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const isAdminHR = ['admin', 'hr'].includes(user?.role);
  const isRM = user?.role === 'reporting_manager';
  const [cycles, setCycles] = useState([]);
  const [teamReviews, setTeamReviews] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [newCycleOpen, setNewCycleOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);  // review obj
  const [form, setForm] = useState({ name: '', quarter: 1, year: new Date().getFullYear(), due_date: '' });
  const [reviewForm, setReviewForm] = useState({});

  const load = async () => {
    try {
      const c = await api.get('/review-cycles');
      setCycles(c.data);
    } catch (e) { /* ignore — empty list */ }
    if (isRM || isAdminHR) {
      try {
        const t = await api.get('/reviews/team');
        setTeamReviews(t.data);
      } catch (e) { /* ignore for users without team scope */ }
    }
    try {
      const m = await api.get('/reviews/me');
      setMyReviews(m.data);
    } catch (e) { /* ignore */ }
    if (isAdminHR) {
      try {
        const a = await api.get('/reviews/all');
        setAllReviews(a.data);
      } catch (e) { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const createCycle = async () => {
    if (!form.name || !form.due_date) return toast.error('Fill all fields');
    try {
      const { data } = await api.post('/review-cycles', form);
      toast.success(`Cycle created — ${data.reviews_created} review${data.reviews_created !== 1 ? 's' : ''} assigned`);
      setNewCycleOpen(false);
      setForm({ name: '', quarter: 1, year: new Date().getFullYear(), due_date: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReview = (rev) => {
    setReviewForm({
      ratings: rev.ratings || {}, overall: rev.overall || 0,
      strengths: rev.strengths || '', improvements: rev.improvements || '', comments: rev.comments || '',
    });
    setEditOpen(rev);
  };

  const submitReview = async (status) => {
    try {
      await api.patch(`/reviews/${editOpen.id}`, { ...reviewForm, status });
      toast.success(status === 'submitted' ? 'Submitted to employee' : 'Saved');
      setEditOpen(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ack = async (rid) => {
    try { await api.post(`/reviews/${rid}/acknowledge`); toast.success('Acknowledged'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Card = ({ r, showEmployee, actions }) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`review-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{r.cycle_name}</div>
          {showEmployee && <div className="font-heading font-semibold text-slate-900 mt-1">{r.employee_name}</div>}
          <div className="text-xs text-slate-500 mt-0.5">Due {r.due_date}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${
          r.status === 'pending' ? 'bg-slate-100 text-slate-600 border-slate-200' :
          r.status === 'submitted' ? 'bg-amber-50 text-amber-700 border-amber-200' :
          'bg-emerald-50 text-emerald-700 border-emerald-200'
        }`}>{r.status}</span>
      </div>
      {r.overall && (
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-slate-500">Overall</span>
          <StarRow value={r.overall} />
        </div>
      )}
      {actions}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Performance</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Reviews</h1>
        </div>
        {isAdminHR && (
          <Dialog open={newCycleOpen} onOpenChange={setNewCycleOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-cycle-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                <Plus className="w-4 h-4 mr-2" /> New review cycle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="new-cycle-dialog">
              <DialogHeader><DialogTitle className="font-heading">Start review cycle</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Name</Label>
                  <Input data-testid="cycle-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Q1 2026" className="rounded-xl mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Quarter</Label>
                    <Select value={String(form.quarter)} onValueChange={(v) => setForm({...form, quarter: +v})}>
                      <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4].map(q => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Year</Label>
                    <Input type="number" data-testid="cycle-year" value={form.year} onChange={(e) => setForm({...form, year: +e.target.value})} className="rounded-xl mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Due date</Label>
                  <Input type="date" data-testid="cycle-due" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="rounded-xl mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewCycleOpen(false)} className="rounded-full">Cancel</Button>
                <Button onClick={createCycle} data-testid="submit-cycle" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">Create & assign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="me">
        <TabsList className="rounded-full bg-slate-100 p-1 h-auto">
          <TabsTrigger value="me" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-my-reviews">My reviews</TabsTrigger>
          {(isRM || isAdminHR) && <TabsTrigger value="team" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-team-reviews">To write</TabsTrigger>}
          {isAdminHR && <TabsTrigger value="all" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-all-reviews">All</TabsTrigger>}
        </TabsList>

        <TabsContent value="me" className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {myReviews.map(r => (
            <Card key={r.id} r={r} showEmployee={false} actions={
              <div className="mt-4">
                {r.status === 'submitted' && (
                  <Button size="sm" onClick={() => ack(r.id)} data-testid={`ack-${r.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="w-4 h-4 mr-1" /> Acknowledge
                  </Button>
                )}
                {r.comments && (
                  <div className="mt-3 text-sm bg-slate-50 rounded-lg p-3 text-slate-600">
                    <div className="text-xs uppercase font-bold text-slate-500 mb-1">Comments</div>
                    {r.comments}
                  </div>
                )}
              </div>
            } />
          ))}
          {myReviews.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No reviews assigned to you yet.</div>}
        </TabsContent>

        {(isRM || isAdminHR) && (
          <TabsContent value="team" className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {teamReviews.map(r => (
              <Card key={r.id} r={r} showEmployee={true} actions={
                <div className="mt-4">
                  <Button size="sm" onClick={() => openReview(r)} data-testid={`open-review-${r.id}`} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white">
                    {r.status === 'pending' ? 'Write review' : 'Edit review'}
                  </Button>
                </div>
              } />
            ))}
            {teamReviews.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No reviews to write.</div>}
          </TabsContent>
        )}

        {isAdminHR && (
          <TabsContent value="all" className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {allReviews.map(r => <Card key={r.id} r={r} showEmployee={true} actions={null} />)}
            {allReviews.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No reviews yet.</div>}
          </TabsContent>
        )}
      </Tabs>

      {/* Edit review dialog */}
      <Dialog open={!!editOpen} onOpenChange={(o) => !o && setEditOpen(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="review-form-dialog">
          {editOpen && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editOpen.employee_name} · {editOpen.cycle_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-3 block">Ratings (1-5)</Label>
                  <div className="space-y-2">
                    {CATEGORIES.map(c => (
                      <div key={c.k} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <span className="text-sm font-medium text-slate-700">{c.label}</span>
                        <StarRow value={reviewForm.ratings?.[c.k] || 0}
                                 onChange={(v) => setReviewForm({...reviewForm, ratings: {...reviewForm.ratings, [c.k]: v}})}
                                 testId={`rating-${c.k}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Overall rating</Label>
                  <div className="mt-2"><StarRow value={reviewForm.overall} onChange={(v) => setReviewForm({...reviewForm, overall: v})} testId="rating-overall" /></div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Strengths</Label>
                  <Textarea data-testid="rev-strengths" rows={2} value={reviewForm.strengths} onChange={(e) => setReviewForm({...reviewForm, strengths: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Areas to improve</Label>
                  <Textarea data-testid="rev-improvements" rows={2} value={reviewForm.improvements} onChange={(e) => setReviewForm({...reviewForm, improvements: e.target.value})} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Comments for employee</Label>
                  <Textarea data-testid="rev-comments" rows={3} value={reviewForm.comments} onChange={(e) => setReviewForm({...reviewForm, comments: e.target.value})} className="rounded-xl mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => submitReview('pending')} data-testid="save-draft" className="rounded-full">Save draft</Button>
                <Button onClick={() => submitReview('submitted')} disabled={!reviewForm.overall} data-testid="submit-review" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  Submit to employee
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
